module Client.Page.User.ScabData

open System
open Client.Infrastructure.Api
open Client.Components
open Client.Components.Graph.Scab
open Client.Domain
open Client.InfrastructureTypes
open Client.DomainTypes.Msg
open Elmish
open Fable.React
open Feliz
open Fulma
open Shared.Dto.Page
open Shared.Dto.Dto
open Shared.Infrastructure
open Client.Components.GraphCommon

let private errorConfirmationConfig: ErrorConfirmation.Config<Msg> = {
    Handler = ErrorConfirmation.createHandler (ScabDataMsg.Reload >> ScabData)
    TimeoutMilliseconds = 5000
}

type DataModel = {
    RequestRunning: bool
    DateRangePicker: DateRangePicker.Data
    Session: UserSession
    SensorId: int
    Station: ScabData.StationDto
    GraphData: Loadable<ScabGraph, unit>
}

type LoadingModel = {
    Session: UserSession
    SensorId: int
}

type Model = Loadable<DataModel, LoadingModel>

let init id (session: UserSession) : Model * Cmd<Msg> =
    let requestData = {
        SessionKey = session.SessionKey
        Data = id
    }

    let cmd =
        Cmd.OfAsync.either
            api.getScabStation
            requestData
            (ScabDataMsg.StationReceived >> Msg.ScabData)
            (ScabDataMsg.LoadingStationFailed >> Msg.ScabData)

    Loadable.Loading(
        {
            Session = session
            SensorId = id
        }
    ),
    cmd

let private createRequestDataCommand (request: AuthenticatedRequest<ScabData.DataRequest>) : Cmd<Msg> =
    Cmd.OfAsync.either
        api.getScabData
        request
        (ScabDataMsg.GraphDataReceived >> Msg.ScabData)
        (ScabDataMsg.LoadingDataFailed >> Msg.ScabData)

let private stationResponseToDataModel
    (loadingModel: LoadingModel)
    (response: AuthenticatedResponse<ScabData.StationResponse>)
    : Model * Cmd<Msg> =
    match response with
    | Ok station ->
        match station with
        | ScabData.Data data ->
            let startTimestamp = DateTime.Now |> DateTime.addDays -2 |> DateTime.startOfDay
            let endTimestamp = DateTime.Now |> DateTime.addDays 1 |> DateTime.endOfDay

            let requestData: AuthenticatedRequest<ScabData.DataRequest> = {
                SessionKey = loadingModel.Session.SessionKey
                Data = {
                    StationId = loadingModel.SensorId
                    StartTimestamp = startTimestamp
                    EndTimestamp = endTimestamp |> DateTime.endOfDay
                }
            }

            Loadable.Data {
                RequestRunning = true
                DateRangePicker = DateRangePicker.initWithRange startTimestamp endTimestamp (TimeSpan.FromDays 31) true
                Session = loadingModel.Session
                SensorId = loadingModel.SensorId
                Station = data
                GraphData = Loadable.Loading()
            },
            createRequestDataCommand requestData
        | ScabData.NotFound -> Loadable.Error "Schorf-Station nicht gefunden", Cmd.none
    | AuthenticatedResponse.Error authError ->
        match authError with
        | AuthErr Unauthenticated -> Loadable.Error "Sie sind nicht eingeloggt, bitte die Seite neu laden", Cmd.none
        | AuthErr Unauthorized -> Loadable.Error "Schorf-Station nicht gefunden", Cmd.none
        | CustomErr err -> Loadable.Error "Beim Laden der Schorf-Station ist ein Fehler aufgetreten", Cmd.none


let update (msg: ScabDataMsg) (model: Model) : Model * Cmd<Msg> =
    match msg, model with
    | ScabDataMsg.StationReceived stationResponse, Loadable.Loading loadingModel ->
        stationResponseToDataModel loadingModel stationResponse
    | ScabDataMsg.GraphDataReceived dataResponse, Loadable.Data dataModel ->
        match dataResponse with
        | Ok(ScabData.DataResponse.Data data) ->
            let graphData =
                if List.isEmpty data then
                    ScabGraph.NoData
                else
                    ScabGraph.Data(createScabGraphData data)

            Loadable.Data {
                dataModel with
                    RequestRunning = false
                    GraphData = Loadable.Data graphData
            },
            Cmd.none
        | Ok(ScabData.DataResponse.NotFound) ->
            let cmd = ErrorConfirmation.responseError errorConfirmationConfig (CustomErr "")

            Loadable.Data { dataModel with RequestRunning = false }, cmd
        | AuthenticatedResponse.Error err ->
            let cmd = ErrorConfirmation.responseError errorConfirmationConfig err

            Loadable.Data { dataModel with RequestRunning = false }, cmd
    | ScabDataMsg.GraphDateRangeChanged dates, Loadable.Data dataModel ->
        let newPickerModel = DateRangePicker.updateData dates dataModel.DateRangePicker

        Loadable.Data { dataModel with DateRangePicker = newPickerModel }, Cmd.none
    | ScabDataMsg.RequestData request, Loadable.Data dataModel ->
        let authenticatedRequest = {
            SessionKey = dataModel.Session.SessionKey
            Data = request
        }

        Loadable.Data { dataModel with RequestRunning = true }, createRequestDataCommand authenticatedRequest
    | ScabDataMsg.LoadingStationFailed _, _ -> Loadable.Error ErrorConfirmation.defaultErrorMessage, Cmd.none
    | ScabDataMsg.LoadingDataFailed ex, Loadable.Data dataModel ->
        let cmd =
            ErrorConfirmation.dataTransmissionErrorConfirmation errorConfirmationConfig ex

        Loadable.Data dataModel, cmd
    | ScabDataMsg.Reload reload, _ ->
        ErrorConfirmation.doReload reload

        model, Cmd.none
    | _, _ -> model, Cmd.none

let private graphWrappingView (graphs: ReactElement list) = div [] [ yield! graphs ]

let private boxHeader: ReactElement =
    Heading.h3 [
        Heading.Modifiers [
            Modifier.TextAlignment(Screen.All, TextAlignment.Option.Centered)
        ]
    ] [ str "Visualisierung der Daten" ]

let private createRequestData (stationId: int) (startDate: DateTime) (endDate: DateTime) : ScabData.DataRequest = {
    StationId = stationId
    StartTimestamp = startDate
    EndTimestamp = endDate
}

let createEnabledButtonOptions dispatch (stationId: int) (startDate: DateTime) (endDate: DateTime) =
    let requestData =
        createRequestData stationId (DateTime.startOfDay startDate) (DateTime.endOfDay endDate)

    [
        Button.Disabled false
        Button.OnClick(fun _ -> dispatch (ScabDataMsg.RequestData requestData |> ScabData))
    ]

let private dateRangePicker dispatch (model: DataModel) =
    let buttonOptions =
        Option.map2
            (createEnabledButtonOptions dispatch model.SensorId)
            model.DateRangePicker.Start
            model.DateRangePicker.End
        |> Option.defaultValue [ Button.Disabled true ]

    let buttonOptions =
        List.append buttonOptions [
            Button.IsLink
            Button.IsLoading model.RequestRunning
        ]

    Column.column [ Column.Width(Screen.All, Column.IsFull) ] [
        Columns.columns [ Columns.IsVCentered ] [
            Column.column [ Column.Width(Screen.All, Column.IsNarrow) ] [ p [] [ str "Datumsbereich:" ] ]
            Column.column [] [
                DateRangePicker.view
                    "Von/Bis Datumsbereich auswählen"
                    ((ScabDataMsg.GraphDateRangeChanged >> ScabData) >> dispatch)
                    model.DateRangePicker
            ]
            Column.column [ Column.Width(Screen.All, Column.IsNarrow) ] [ Button.button buttonOptions [ str "Laden" ] ]
        ]
    ]

let private pageHeader (stationName: string) : ReactElement =
    Heading.h1 [
        Heading.Modifiers [
            Modifier.TextAlignment(Screen.All, TextAlignment.Centered)
        ]
    ] [ str stationName ]

let private pageLayout stationName content =
    Container.container [ Container.isFullWidth ] [
        pageHeader stationName
        Columns.columns [ Columns.IsMultiline ] [
            Column.column [ Column.Width(Screen.All, Column.IsFull) ] [ Box.box' [] content ]
        ]
    ]

let private graphsView (graphData: ScabGraph) =
    match graphData with
    | NoData -> [
        createFullColumn (boxStringSubtitle "Es wurden keine Daten im ausgewählten Zeitraum gefunden")
      ]
    | ScabGraph.Data data -> graphs data


let pageView dispatch (model: DataModel) =
    let boxContent = [
        boxHeader
        dateRangePicker dispatch model
        yield! (Loadable.listView graphsView model.GraphData)
    ]

    pageLayout model.Station.Name boxContent


let view dispatch (model: Model) = Loadable.view (pageView dispatch) model